import { useMemo, useState } from 'react';
import { useAtomValue } from 'jotai';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

import { ArrowLeftLine } from '@archipro-design/icons';
import { Calendar } from '@rocco/ui/calendar/components/calendar';
import { cn } from '@rocco/utils/cn';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import {
    GateHeading,
    GateSubcopy,
} from '~/modules-rocco/user/component/auth-modal/GateHeading';
import {
    GateFieldLabel,
    GateSelect,
} from '~/modules-rocco/user/component/auth-modal/GateFormControls';
import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import type { BookingRosterMember } from '../api/get-request-availability.server';
import type { useBookingSchedule } from '../hooks/use-booking-schedule';
import { BookMeetingPersonPicker } from './BookMeetingPersonPicker';
import { getTimezoneShort } from '../utils/calendar';
import {
    meetingNameFor,
    gateHeadingFor,
    type MeetingDetails,
} from '../domain/book-meeting-flow';

dayjs.extend(utc);
dayjs.extend(timezone);

type Booking = ReturnType<typeof useBookingSchedule>;

/** First names read as a person talking; full names read as a record. */
const firstNameOf = (fullName: string): string =>
    fullName.trim().split(/\s+/)[0] || fullName;

/** The gate's primary action, matching SecureSignInOffer's. */
const GatePrimaryButton = ({
    children,
    ...props
}: React.ButtonHTMLAttributes<HTMLButtonElement>) => (
    <button
        type="button"
        className="flex h-14 w-full items-center justify-center rounded-[var(--radius-btn-l)] bg-[#181818] text-[1.125rem] font-medium tracking-[-0.02em] text-[#f3f3f3] shadow-[0_0.1875rem_0.8125rem_0_rgba(0,0,0,0.25),0_1.25rem_2.125rem_0_rgba(0,0,0,0.25)] transition-colors hover:bg-[#2c2c2c] disabled:cursor-not-allowed disabled:opacity-40"
        {...props}
    >
        {children}
    </button>
);

interface BookMeetingGateStepsProps {
    booking: Booking;
    /** Named in the confirming line, so the reader knows who picks it up. */
    subjectName: string;
    /** The practice, named when there is no individual to name. */
    professionalName: string;
    /**
     * No public team member to meet, so the request is of the business. The
     * confirming line has to name the practice rather than address it as a
     * person: "Patterson Associates" is not someone called Patterson.
     */
    isCompany: boolean;
    /** The reader confirmed the day and time. */
    onContinue: () => void;
    /** Back from choosing a day, to the steps before it. Omitted when there are none. */
    onBack?: () => void;
    /**
     * Availability is still in flight. Everything on this step that does not
     * depend on the answer is drawn anyway — only the month grid waits, because
     * the days it can offer are the one thing the fetch decides.
     */
    isLoading?: boolean;
    /**
     * The chosen person keeps a connected calendar, so picking a time books it
     * rather than asking for it. Both kinds now sit in the same picker, so the
     * wording has to follow the choice: offering to "send a request" for a
     * meeting that is confirmed on the spot is simply untrue.
     */
    isBooked?: boolean;
    /**
     * The page that opened this knew a picker was coming, so the loading state
     * reserves its space. Left false when nothing knows: most practices show
     * nobody publicly, and a field that appears and then collapses moves the
     * calendar further than one that simply arrives.
     */
    expectsTeamPicker?: boolean;
    /**
     * Everyone the practice shows publicly. Drives the picker instead of the
     * availability payload, which only ever holds one way of making a meeting:
     * this list spans both, so a reader sees the whole team and the choice
     * decides whether the time is booked or requested.
     */
    roster: BookingRosterMember[];
    selectedTeamMemberId: number | null;
    onSelectTeamMember: (teamMemberId: number | null) => void;
    /** The practice's badge, worn by the "anyone" row. */
    companyLogo?: string | null;
    logoBackgroundColor?: string | null;
    /** The booking link's name, place and note, once availability says. */
    details?: MeetingDetails | null;
    /** Shown above the calendar, e.g. why the reader is choosing again. */
    notice?: string | null;
    /** The words on the button that opened the modal, for its heading. */
    ctaLabel?: string;
}

/** The small back arrow every step of the gate uses. */
const BackButton = ({
    label,
    onClick,
}: {
    label: string;
    onClick: () => void;
}) => (
    <button
        type="button"
        aria-label={label}
        onClick={onClick}
        className="flex size-8 items-center justify-center self-start rounded-[0.75rem] text-[#181818] hover:bg-black/5 disabled:opacity-40"
    >
        <ArrowLeftLine className="size-5" />
    </button>
);

/**
 * Choosing a time, in the gate's own language.
 *
 * Split across two steps rather than stacked into one: the gate's card is
 * 977x635, and a month grid plus two selects plus an action does not sit in it
 * without scrolling. Splitting also lets each step ask one question, which is
 * how every other step in this modal reads.
 *
 * Who the meeting is with lives on the modal's left pane, so nothing here
 * repeats it and the column is the calendar's.
 *
 * Deliberately not the accordion's booking card. That card is a wide two-column
 * panel with its own surface, borders and type scale, and dropping it into the
 * gate read as a different product bolted on. Everything here is
 * {@link GateHeading}, {@link GateFieldLabel} and {@link GateSelect}, so it is
 * the same furniture as the auth steps either side of it. See BC-5174.
 */
export const BookMeetingGateSteps = ({
    booking,
    subjectName,
    professionalName,
    isCompany,
    onContinue,
    onBack,
    isLoading = false,
    isBooked = false,
    expectsTeamPicker = false,
    roster,
    selectedTeamMemberId,
    onSelectTeamMember,
    companyLogo,
    logoBackgroundColor,
    details,
    notice,
    ctaLabel,
}: BookMeetingGateStepsProps) => {
    const [step, setStep] = useState<'date' | 'time'>('date');
    const { isDesktop } = useAtomValue(uiContextAtomDisplayMode);

    const hasDate = Boolean(booking.selectedDateTimestamp);
    const hasSlot = Boolean(booking.selectedSlotTimestamp);

    const slotsForDay = useMemo(() => {
        if (!booking.selectedDateTimestamp) return [];
        const dayKey = dayjs(booking.selectedDateTimestamp)
            .tz(booking.timezoneName)
            .format('YYYY-MM-DD');
        return (
            booking.freeDays.find((day) => day.date === dayKey)?.freeSlots ?? []
        );
    }, [booking.selectedDateTimestamp, booking.timezoneName, booking.freeDays]);

    const formattedDate = booking.selectedDateTimestamp
        ? dayjs(booking.selectedDateTimestamp)
              .tz(booking.timezoneName)
              .format('dddd, D MMMM YYYY')
        : '';

    const timezoneAbbr = getTimezoneShort(
        booking.timezoneName,
        booking.selectedSlotTimestamp,
    );

    // "Anyone" is a real choice, so even a one-name roster needs the field:
    // without it a reader who opened on the practice cannot ask for the one
    // person by name, and one who opened on that person cannot back out.
    const showPersonPicker = roster.length > 0;

    if (step === 'time') {
        return (
            <div className="mx-auto my-auto flex h-full w-full max-w-90.75 flex-col">
                <BackButton
                    label="Back to choosing a day"
                    onClick={() => setStep('date')}
                />
                <GateHeading className="mt-4">What time suits?</GateHeading>
                <GateSubcopy>
                    Choose what works for you, and{' '}
                    {isBooked
                        ? `it goes straight into ${firstNameOf(subjectName)}'s calendar.`
                        : isCompany
                          ? `the team at ${professionalName} will confirm your appointment.`
                          : `${firstNameOf(subjectName)} will take it from there.`}
                </GateSubcopy>
                {/* The day is the one thing carried over from the step before,
                    so it stands on its own line in the body colour rather than
                    buried mid-sentence in the grey supporting copy. */}
                <p className="mt-5 text-base tracking-[-0.02em] text-[#181818]">
                    {isBooked
                        ? 'You\u2019re booking'
                        : 'You\u2019re requesting'}{' '}
                    {meetingNameFor(details?.sessionName)} on{' '}
                    <span className="font-medium">{formattedDate}</span>.
                </p>
                {details ? (
                    <p className="mt-1.5 text-[0.8125rem] tracking-[-0.02em] text-[#828282]">
                        {details.location.label}
                    </p>
                ) : null}

                <div className="mt-8 space-y-5">
                    <div className="space-y-2">
                        <GateFieldLabel htmlFor="book-meeting-duration">
                            Meeting length
                        </GateFieldLabel>
                        <GateSelect
                            id="book-meeting-duration"
                            value={String(booking.selectedDuration)}
                            onChange={(event) =>
                                booking.handleDurationChange(
                                    Number(event.target.value),
                                )
                            }
                        >
                            {booking.durations.map((duration) => (
                                <option key={duration} value={String(duration)}>
                                    {duration} minutes
                                </option>
                            ))}
                        </GateSelect>
                    </div>

                    <div className="space-y-2">
                        <GateFieldLabel htmlFor="book-meeting-time">
                            Start time
                        </GateFieldLabel>
                        <GateSelect
                            id="book-meeting-time"
                            value={
                                booking.selectedSlotTimestamp
                                    ? String(booking.selectedSlotTimestamp)
                                    : ''
                            }
                            onChange={(event) =>
                                booking.handleTimeSelectWithAssignment(
                                    Number(event.target.value),
                                )
                            }
                        >
                            <option value="" disabled>
                                Select a time
                            </option>
                            {slotsForDay.map((slot) => (
                                <option
                                    key={slot.timestamp}
                                    value={String(slot.timestamp)}
                                >
                                    {slot.label}
                                </option>
                            ))}
                        </GateSelect>
                        <p className="text-[0.75rem] tracking-[-0.02em] text-[#828282]">
                            Times shown in {timezoneAbbr || 'your local time'}.
                        </p>
                    </div>
                </div>

                <div className="flex-1" />

                <GateStickyFooter desktopClassName="mb-9">
                    <GatePrimaryButton disabled={!hasSlot} onClick={onContinue}>
                        Continue
                    </GatePrimaryButton>
                </GateStickyFooter>
            </div>
        );
    }

    return (
        <div className="mx-auto my-auto flex h-full w-full max-w-90.75 flex-col">
            {onBack ? (
                <BackButton label="Back to your answers" onClick={onBack} />
            ) : null}
            {/* Named for the thing being done, matching the CTA that opened
                the modal. "Let's find a time" described the step rather than
                the errand, which read oddly as the opening line of two. */}
            <GateHeading className={onBack ? 'mt-4' : 'mt-3'}>
                {gateHeadingFor(details?.sessionName, ctaLabel)}
            </GateHeading>
            <GateSubcopy>
                {/* While loading this reads as the practice's, matching the
                    left pane: it is the common case, and it is the line that
                    stays true if the answer turns out to have no one to name. */}
                {/* The picker's line while its field is reserved too: the
                    practice's line runs to three where this runs to two, and
                    the swap moved everything under it by 19px. */}
                {showPersonPicker || (isLoading && expectsTeamPicker)
                    ? "Choose who you'd like to meet and a day that suits you."
                    : isBooked
                      ? `Pick a day that suits and it is booked with ${firstNameOf(subjectName)}.`
                      : isCompany || isLoading
                        ? `Pick a day that suits and the team at ${professionalName} will be in touch to confirm.`
                        : `Pick a day that suits and ${firstNameOf(subjectName)} will confirm.`}
            </GateSubcopy>

            {/* The practice's own words for this link. The pane carries them
                on desktop too, but the sheet drops the pane. */}
            {details?.customMessage && !isDesktop ? (
                <p className="mt-3 text-[0.875rem] leading-snug tracking-[-0.02em] whitespace-pre-wrap text-[#656565]">
                    {details.customMessage}
                </p>
            ) : null}

            {notice ? (
                <p
                    role="status"
                    className="mt-4 rounded-[1rem] bg-[#f6f6f6] px-4 py-3 text-[0.875rem] leading-snug tracking-[-0.02em] text-[#161616]"
                >
                    {notice}
                </p>
            ) : null}

            {isLoading && expectsTeamPicker ? (
                <div className="mt-7 space-y-2" aria-hidden>
                    <div className="h-3 w-44 animate-pulse rounded bg-black/8" />
                    <div className="h-10.5 w-full animate-pulse rounded-[1rem] bg-black/6" />
                </div>
            ) : showPersonPicker ? (
                <div className="mt-7 space-y-2">
                    <GateFieldLabel htmlFor="book-meeting-person">
                        Who would you like to meet?
                    </GateFieldLabel>
                    <BookMeetingPersonPicker
                        id="book-meeting-person"
                        roster={roster}
                        selectedTeamMemberId={selectedTeamMemberId}
                        onSelect={onSelectTeamMember}
                        professionalName={professionalName}
                        companyLogo={companyLogo}
                        logoBackgroundColor={logoBackgroundColor}
                    />
                </div>
            ) : null}

            {/* Boxed like the selects above it, so the calendar reads as the
                step's second field rather than a panel of its own. It carries
                no label: the month heading says what it is. */}
            <div
                className={cn(
                    showPersonPicker || (isLoading && expectsTeamPicker)
                        ? 'mt-5'
                        : 'mt-6',
                )}
            >
                <div className="rounded-[1rem] bg-[#f6f6f6] px-3 py-3">
                    {isLoading ? (
                        <MonthGridSkeleton />
                    ) : (
                        <Calendar
                            variant="light"
                            naviVariant="right"
                            density="compact"
                            timeZone={booking.timezoneName}
                            freeDays={booking.freeDays}
                            selectedTimestamp={
                                booking.selectedDateTimestamp ?? undefined
                            }
                            onDateSelect={booking.handleCalendarSelect}
                        />
                    )}
                </div>
            </div>

            {!isLoading &&
            selectedTeamMemberId != null &&
            booking.freeDays.length === 0 ? (
                <p className="mt-4 text-[0.8125rem] text-destructive">
                    No times for{' '}
                    {roster.find(
                        (member) =>
                            member.teamMemberId === selectedTeamMemberId,
                    )?.name ?? 'this person'}{' '}
                    right now. Try someone else on the team.
                </p>
            ) : null}

            <div className="flex-1" />

            {/* A month grid is taller than the 977x635 card, so this step's
                action has to pin on desktop too or it sits under the fold. */}
            <GateStickyFooter pinOn="both" desktopClassName="mb-9">
                <GatePrimaryButton
                    disabled={!hasDate || isLoading}
                    onClick={() => setStep('time')}
                >
                    Continue
                </GatePrimaryButton>
            </GateStickyFooter>
        </div>
    );
};

/**
 * The month grid's shape while availability loads, inside the same grey field
 * the real calendar sits in.
 *
 * Built to the compact calendar's own box rather than to a guess: a 20px header
 * over the same rule, a 14px weekday row, and five rows of 22px cells — the
 * shape September opens on. The first pass drew six rows of 16px cells with
 * gaps between them, which stood ~20px taller than what replaced it.
 */
const MonthGridSkeleton = () => (
    <div aria-hidden>
        <div className="mb-2 flex items-center justify-between border-b border-black/8 px-2 pb-2">
            <div className="h-[1.25rem] w-24 animate-pulse rounded bg-black/8" />
            <div className="size-4 animate-pulse rounded bg-black/8" />
        </div>
        <div className="grid grid-cols-7">
            {Array.from({ length: 7 }).map((_, index) => (
                <div
                    key={`weekday-${index}`}
                    className="flex h-[1.3125rem] items-center justify-center"
                >
                    <div className="h-2 w-5 animate-pulse rounded bg-black/8" />
                </div>
            ))}
        </div>
        <div className="grid grid-cols-7">
            {Array.from({ length: 35 }).map((_, index) => (
                <div
                    key={`day-${index}`}
                    // Same box as a real day cell — the padding and type scale
                    // the compact calendar uses, around an invisible digit that
                    // gives the row the identical line height. Setting a height
                    // in rem instead landed a pixel short on every row, which
                    // put the action six pixels out by the fifth.
                    className="relative py-1.5 text-center text-3.5"
                >
                    <span className="invisible">0</span>
                    <span className="absolute inset-0 m-auto size-[1.3125rem] animate-pulse rounded-full bg-black/5" />
                </div>
            ))}
        </div>
        <span className="sr-only">Finding available days</span>
    </div>
);
